/**
 * Positions — slim orchestrator (~150 lines).
 * State management + API calls live here; rendering is delegated to sub-components.
 */
import { useEffect, useState } from 'react';
import { useToast } from '../ui/Toast';
import api from '../../services/api';
import { styles } from '../../styles';
import TradingViewChart from './TradingViewChart';
import PositionTable from './PositionTable';

const Positions = () => {
  const toast = useToast();
  const [positions, setPositions] = useState([]);
  const [sort, setSort] = useState({ orderBy: 'first_entry_time', order: 'DESC' });
  const [loading, setLoading] = useState(true);
  const [expandedPositions, setExpandedPositions] = useState(new Set());
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchPositions();
  }, []); // eslint-disable-line

  const fetchPositions = async (orderBy = sort.orderBy, order = sort.order) => {
    try {
      const params = new URLSearchParams({ orderBy, order });
      const { data } = await api.get(`/positions?${params}`);
      if (data.success) setPositions(data.data || []);
    } catch (error) {
      console.error('Failed to fetch positions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    const newOrder = sort.orderBy === column && sort.order === 'ASC' ? 'DESC' : 'ASC';
    setSort({ orderBy: column, order: newOrder });
    fetchPositions(column, newOrder);
  };

  const createPositionsFromTrades = async () => {
    try {
      setLoading(true);
      const { data } = await api.post('/positions/create-from-trades');
      if (data.success) {
        toast.success(`Created ${data.data.length} positions from trades`);
        fetchPositions();
      }
    } catch (error) {
      console.error('Failed to create positions:', error);
      toast.error('Failed to create positions from trades');
    } finally {
      setLoading(false);
    }
  };

  const deleteAllPositions = async () => {
    try {
      setLoading(true);
      setShowDeleteModal(false);
      const { data } = await api.delete('/positions/delete-all');
      if (data.success) {
        toast.info(data.message);
        fetchPositions();
      }
    } catch (error) {
      console.error('Failed to delete positions:', error);
      toast.error('Failed to delete all positions');
    } finally {
      setLoading(false);
    }
  };

  const togglePositionExpansion = (positionId) => {
    setExpandedPositions((prev) => {
      const next = new Set(prev);
      next.has(positionId) ? next.delete(positionId) : next.add(positionId);
      return next;
    });
  };

  const openChart = (symbol, underlyingSymbol, trades) => {
    setSelectedSymbol({ symbol: underlyingSymbol || symbol, trades });
    setShowChart(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-16 text-slate-400">
        <div className="text-center">
          <div className="text-4xl mb-4 animate-pulse">📊</div>
          <p>Loading positions...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f8fafc', margin: 0 }}>Positions</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {positions.length > 0 && (
            <button
              onClick={() => setShowDeleteModal(true)}
              style={{ ...styles.button, backgroundColor: '#dc2626', borderColor: '#dc2626' }}
            >
              Delete All Positions
            </button>
          )}
          <button onClick={createPositionsFromTrades} style={styles.button}>
            Create Positions from Trades
          </button>
        </div>
      </div>

      {/* Count */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center' }}>
        <span style={{ fontSize: '0.875rem', color: '#94a3b8' }}>{positions.length} positions total</span>
      </div>

      {/* Chart overlay */}
      {showChart && selectedSymbol && (
        <TradingViewChart
          symbol={selectedSymbol.symbol}
          trades={selectedSymbol.trades}
          onClose={() => { setShowChart(false); setSelectedSymbol(null); }}
        />
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={{ marginTop: 0, color: 'var(--text-primary)' }}>Confirm Deletion</h2>
            <p style={{ color: 'var(--text-secondary)', margin: '1rem 0' }}>
              Are you sure you want to delete ALL {positions.length} positions?
            </p>
            <p
              style={{
                color: 'var(--text-secondary)',
                fontSize: '0.875rem',
                backgroundColor: '#f3f4f6',
                padding: '0.75rem',
                borderRadius: '0.375rem',
                border: '1px solid #e5e7eb',
              }}
            >
              <strong>Note:</strong> This will unlink all trades from positions but won&apos;t delete the trades
              themselves. You can recreate positions afterwards.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{ ...styles.button, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db' }}
              >
                Cancel
              </button>
              <button
                onClick={deleteAllPositions}
                style={{ ...styles.button, backgroundColor: '#dc2626', color: 'white' }}
              >
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main table */}
      <div style={styles.card}>
        <PositionTable
          positions={positions}
          sort={sort}
          onSort={handleSort}
          expandedPositions={expandedPositions}
          onToggleExpand={togglePositionExpansion}
          onOpenChart={openChart}
        />
      </div>
    </div>
  );
};

export default Positions;
